/**
 * Life4Earth V2 — React hook for workspace AI interaction data.
 *
 * Loads AI sessions, evidence items, and unanswered questions for a workspace.
 * Automatically refreshes when the workspace AI cache is invalidated
 * (e.g. after a session is completed or evidence is updated via SignalR).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAiSessions,
  fetchEvidenceItems,
  fetchUnansweredQuestions,
} from "./workspaceAi";
import type {
  AiSessionResponse,
  EvidenceItemResponse,
  ClarifyingQuestionResponse,
} from "./workspaceAi";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

// ── Result shape ────────────────────────────────────────────

export interface UseApiWorkspaceAiResult {
  /** All AI sessions for the workspace (most recent first). */
  sessions: AiSessionResponse[];
  /** All evidence items for the workspace (ordered by priority). */
  evidenceItems: EvidenceItemResponse[];
  /** Unanswered clarifying questions across all sessions. */
  unansweredQuestions: ClarifyingQuestionResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  /** Force a refetch (e.g. after a local mutation). */
  refetch: () => void;
}

interface CachedWorkspaceAiData {
  sessions: AiSessionResponse[];
  evidenceItems: EvidenceItemResponse[];
  unansweredQuestions: ClarifyingQuestionResponse[];
}

// ── Hook ────────────────────────────────────────────────────

/**
 * Load AI interaction data for a single workspace.
 * Pass null/undefined workspaceGuid to skip loading.
 */
export function useApiWorkspaceAi(
  workspaceGuid: string | null | undefined
): UseApiWorkspaceAiResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiWorkspaceAiResult>({
    sessions: [],
    evidenceItems: [],
    unansweredQuestions: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const cacheKey = workspaceGuid
    ? `${QUERY_KEYS.workspaceAi}:${workspaceGuid}`
    : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !workspaceGuid) return;

    try {
      const cached = await fetchForKey(
        cacheKey!,
        async (): Promise<CachedWorkspaceAiData> => {
          const [sessions, evidenceItems, unansweredQuestions] =
            await Promise.all([
              fetchAiSessions(workspaceGuid),
              fetchEvidenceItems(workspaceGuid),
              fetchUnansweredQuestions(workspaceGuid),
            ]);
          return { sessions, evidenceItems, unansweredQuestions };
        },
        { force: true }
      );

      if (mountedRef.current) {
        setData({
          sessions: cached.sessions,
          evidenceItems: cached.evidenceItems,
          unansweredQuestions: cached.unansweredQuestions,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          sessions: [],
          evidenceItems: [],
          unansweredQuestions: [],
          loading: false,
          error: err.message,
          source: "empty",
          refetch,
        });
      }
    }
  }, [workspaceGuid, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;

    if (!workspaceGuid) {
      setData({
        sessions: [],
        evidenceItems: [],
        unansweredQuestions: [],
        loading: false,
        error: null,
        source: "empty",
        refetch: () => {},
      });
      return;
    }

    async function load() {
      try {
        const cached = await fetchForKey(
          cacheKey!,
          async (): Promise<CachedWorkspaceAiData> => {
            const [sessions, evidenceItems, unansweredQuestions] =
              await Promise.all([
                fetchAiSessions(workspaceGuid!),
                fetchEvidenceItems(workspaceGuid!),
                fetchUnansweredQuestions(workspaceGuid!),
              ]);
            return { sessions, evidenceItems, unansweredQuestions };
          }
        );

        if (mountedRef.current) {
          setData({
            sessions: cached.sessions,
            evidenceItems: cached.evidenceItems,
            unansweredQuestions: cached.unansweredQuestions,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            sessions: [],
            evidenceItems: [],
            unansweredQuestions: [],
            loading: false,
            error: err.message,
            source: "empty",
            refetch,
          });
        }
      }
    }

    load();

    // Listen for SignalR-triggered cache invalidation.
    // WorkspaceUpdated events invalidate `QUERY_KEYS.workspaceAi`,
    // but we scope to our specific workspace's cache key.
    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const key = customEvent.detail?.key;
      if (key === QUERY_KEYS.workspaceAi || key === cacheKey) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [workspaceGuid, cacheKey, refetch]);

  return data;
}
